import { EmptyObject } from '@reduxjs/toolkit';
import { IErrorLogger } from '@verv-inc/js-toolkit/core-logs/lib/errorLogger';
import { OnApproveData } from '@paypal/paypal-js';
import * as Sentry from "@sentry/react";
import { analyticsEventsTracker, EVENT_TRACKERS, GTMOnboardingFunnelEvent, GTMQuizFunnelEvent } from '../analytics';
import errorLogger from '../errorLogger/appErrorLogger';
import {
  PaypalConfigResponse,
  StripeConfigResponse,
  PayPalRegisterSubscriptionRequest,
  PayPalRegisterSubscriptionRequestV2,
  PayPalRegisterSubscriptionV2Response,
  PayPalRegisterSubscriptionResponse,
  StripeCreateSubscriptionRequest,
  StripeCheckoutSubscriptionResponse,
  WebCatalogProduct,
  WebCatalogConfigResponse,
  APIError,
  WebCatalogDigitalProductList,
  StripePurchaseDigitalProductRequest,
  WebCatalogDigitalProductOferList,
  PayPalСreateDigitalProductRequest,
  PayPalRegisterDigitalProductRequest,
} from './EveryDoggyAPITypes';
import { getInternalProductId } from '../scripts/getInternalProductId';
import { Environment } from '../../types';
import getAmplitudeDeviceId from '../scripts/amplitudeDeviceId';

// Tracks whether the page is being torn down. A "Failed to fetch" thrown while the
// page is unloading or backgrounded (common in in-app browsers / on PayPal redirect
// return) is a benign aborted request, not a real network/CORS problem — we use this
// to avoid reporting that noise to Sentry. `navigator.onLine` alone doesn't catch it
// because it's hardcoded `true` in many WebViews.
let isPageUnloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { isPageUnloading = true; });
  window.addEventListener('beforeunload', () => { isPageUnloading = true; });
  // Reset on bfcache restore so later failures are reported normally.
  window.addEventListener('pageshow', () => { isPageUnloading = false; });
}

function isPageActive(): boolean {
  if (isPageUnloading) return false;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return true;
}

// Config fetches are retried with backoff to ride out transient drops (in-app
// WebViews killing in-flight requests during the redirect handoff, mobile
// network blips) before we give up and report to Sentry.
const CONFIG_FETCH_ATTEMPTS = 3;
// 4s, not 8s: the checkout view-models still call getStripeConfig twice in a row
// (`if (!data) data = await …`), so a hanging server costs two full retry chains.
// At 4s the worst case is ~26s instead of ~50s. Raise this back to 8s once that
// caller-side retry is removed — the helper already retries on its own.
const CONFIG_FETCH_TIMEOUT_MS = 4000;
const CONFIG_FETCH_RETRY_BASE_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => { setTimeout(resolve, ms); });
}

interface ConfigFetchOptions {
  /** Sentry `module` tag and first fingerprint segment — must match the previous
   *  per-method values or the existing issues regroup. */
  module: string;
  /** Merged into Sentry `extra` on every report. */
  extra?: Record<string, any>;
  /** Some endpoints intentionally stay silent on a non-2xx. */
  reportHttpError?: boolean;
  reportParseError?: boolean;
  /** Runs inside the parse try/catch so a throwing adapter is still reported as
   *  parse_error instead of escaping to the caller. */
  transform?: (raw: any) => any;
}

// Shared by every config/catalog GET: idempotent requests whose failures are mostly
// transient, so a short backoff recovers them and Sentry hears about a call once,
// after the attempts are exhausted — not once per attempt.
async function fetchConfigWithRetry(url: string, options: ConfigFetchOptions): Promise<any> {
  const {
    module,
    extra = {},
    reportHttpError = true,
    reportParseError = true,
    transform,
  } = options;
  const isProd = process.env.ENVIRONMENT === Environment.prod;

  for (let attempt = 1; attempt <= CONFIG_FETCH_ATTEMPTS; attempt += 1) {
    const isLastAttempt = attempt === CONFIG_FETCH_ATTEMPTS;
    let response: Response;

    try {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), CONFIG_FETCH_TIMEOUT_MS);
      try {
        response = await fetch(url, { signal: abortController.signal });
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (networkError) {
      // Offline, or the page is being torn down / backgrounded: a benign aborted
      // request. Not worth a retry, and not worth reporting.
      const recoverable = navigator.onLine && isPageActive();

      if (!isLastAttempt && recoverable) {
        await delay(CONFIG_FETCH_RETRY_BASE_MS * 2 ** (attempt - 1));
        continue;
      }

      if (isProd && recoverable) {
        Sentry.captureException(networkError, {
          level: 'warning',
          extra: {
            ...extra,
            attempts: attempt,
            online: navigator.onLine,
            path: window.location.pathname,
          },
          tags: {
            module,
            error_type: 'network_error',
          },
          fingerprint: [module, 'network_error'],
        });
      }
      return null;
    }

    // 5xx may be transient; a 4xx is deterministic and will not change on retry.
    if (!response.ok) {
      if (response.status >= 500 && !isLastAttempt) {
        await delay(CONFIG_FETCH_RETRY_BASE_MS * 2 ** (attempt - 1));
        continue;
      }

      if (isProd && reportHttpError) {
        Sentry.captureException(new Error(`${module}: HTTP ${response.status}`), {
          level: 'error',
          extra: {
            ...extra,
            attempts: attempt,
            http_status: response.status,
            status: response.status,
            path: window.location.pathname,
          },
          tags: {
            module,
            error_type: 'http_error',
          },
          fingerprint: [module, 'http_error', String(response.status)],
        });
      }
      return null;
    }

    try {
      const raw = await response.json();
      return transform ? transform(raw) : raw;
    } catch (parseError) {
      if (isProd && reportParseError) {
        Sentry.captureException(parseError, {
          level: 'error',
          extra: {
            ...extra,
            attempts: attempt,
            http_status: response.status,
            status: response.status,
          },
          tags: { module, error_type: 'parse_error' },
          fingerprint: [module, 'parse_error'],
        });
      }
      return null;
    }
  }

  return null;
}

export interface IEveryDoggyAPIServices {
  location?: string;
  getPaypalConfig: () => Promise<PaypalConfigResponse | null>;
  getStripeConfig: () => Promise<StripeConfigResponse | null>;
  registerPaypalSubscription: (dataToSend: PayPalRegisterSubscriptionRequest, paymentType: string, productName: string, price: string) => Promise<EmptyObject | APIError>;
  registerPaypalSubscriptionV2: (dataToSend: PayPalRegisterSubscriptionRequestV2, paymentType: string, productName: string, price: string) => Promise<PayPalRegisterSubscriptionV2Response | APIError>;
  trackPaypalCompletePaymentEvent: (subscriptionID: string, paymentType: string, productName: string, price: string) => Promise<EmptyObject>;
  checkoutPaypalSubscription: (subscriptionID: string) => Promise<PayPalRegisterSubscriptionResponse>;
  createStripeSubscription: (dataToSend: StripeCreateSubscriptionRequest, paymentType: string, productName: string, price: string, email: string, stripe?: any) => Promise<EmptyObject | APIError>;
  checkoutStripeSubscription: (email: string) => Promise<StripeCheckoutSubscriptionResponse>;
  getWebCatalogProductConfig: () => Promise<WebCatalogConfigResponse>;
  getWebCatalogProduct: (productId: string) => Promise<WebCatalogProduct | null>;
  getWebCatalogDigitalProductList: () => Promise<WebCatalogDigitalProductList>,
  purchaseStripeDigitalProduct: (dataToSend: StripePurchaseDigitalProductRequest, paymentType: string, productName: string) => Promise<EmptyObject | APIError>;
  registerPaypalOrder: (dataToSend: PayPalRegisterDigitalProductRequest) => Promise<EmptyObject | APIError>;
  createOrderPaypal: (dataToSend: PayPalСreateDigitalProductRequest) => Promise<EmptyObject | APIError>;
  getWebCatalogDigitalProductOferList: () =>Promise<WebCatalogDigitalProductOferList>
}

type EDPlanResponse = {
  id: number;
  position: number;
  hidden: boolean;
  default: boolean;
  paypal_plan: string;
  promo_period_interval?: string;
  promo_period_interval_count?: number;
  promo_period_payed_amount?: number;
  main_period_interval?: string;
  main_period_interval_count?: number;
  main_period_payed_amount?: number;
  stripe_promo_product?: {
    id?: string;
  }
  stripe_plan: {
    id: string; // plan id "price_xxxxxxxxxxxx",
    amount: string; //"19.99"
    amount_decimal: string; //"1999.000000000000"
    currency: string; //"usd"
    interval: string; // "month"
    interval_count: number;
    trial_period_days: number | null;
    product: string; // product id "prod_xxxxxxxxxxxx"
  };
  ui_settings: {
    head: string; //"$4.6 per week"
    title: string; // "1-MONTH PLAN"
    price_cents: string; // "60"
    default_title: string; // "EveryDoggy 1-month subscription"
    default_value: string; // "$19.99"
    price_dollars: string; // "4"
  };
};
type EDPlanConfigResponse = EDPlanResponse[];

function WebCatalogProductAdapter(plan: EDPlanResponse): WebCatalogProduct {
  return {
    id: plan.id.toString(),
    position: plan.position,
    amount: plan.main_period_payed_amount != null ? String(plan.main_period_payed_amount) : plan.stripe_plan.amount,
    duration: {
      unit: plan.stripe_plan.interval || String(plan.main_period_interval),
      value: plan.stripe_plan.interval_count || Number(plan.main_period_interval_count),
      trial_period_unit: plan.promo_period_interval || null,
      trial_period_value: plan.promo_period_interval_count || null,
    },
    paypal_plan_id: plan.paypal_plan,
    stripe_plan_id: plan.stripe_promo_product.id || plan.stripe_plan.id,
    trial_period_days: plan.stripe_plan.trial_period_days,
    trial_amount: plan.promo_period_payed_amount ? String(plan.promo_period_payed_amount) : null,
    internal_product_id: getInternalProductId(plan.id),
  };
}

export function ComposeEveryDoggyAPIServices(apiURL: string, errorLogger: IErrorLogger): IEveryDoggyAPIServices {
  const userLanguage = navigator.language || 'en-US';
  return {
    location: apiURL,

    async getPaypalConfig() {
      return fetchConfigWithRetry(`${this.location}/core/api/v1/pp/config`, {
        module: 'getPaypalConfig',
      });
    },

    async registerPaypalSubscription(dataToSend, paymentType, productName, price) {
      const subId = dataToSend?.subscription_id || 'UNKNOWN_SUB_ID';

      if (process.env.ENVIRONMENT === Environment.prod) {
        Sentry.addBreadcrumb({
          message: 'Calling registerPaypalSubscription',
          category: 'api.call',
          data: { subscription_id: subId }
        });
        analyticsEventsTracker.trackWithTarget(
          GTMOnboardingFunnelEvent.paypalRegisterAttempt({
            subscriptionId: subId,
            productName,
          }),
          EVENT_TRACKERS.GTM
        );
        window.dataLayer?.push({
          event: 'paypal_register_attempt',
          subscription_id: subId,
          product_name: productName,
        });
      }

      const response = await fetch(`${this.location}/core/api/v1/plan/success/paypal/create_subscription/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Something went wrong';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      if (response.ok) {
        if (process.env.ENVIRONMENT === Environment.prod) {
          analyticsEventsTracker.trackWithTarget(
            GTMOnboardingFunnelEvent.paypalRegisterSuccess({
              subscriptionId: subId,
              productName,
              httpStatus: response.status,
            }),
            EVENT_TRACKERS.GTM
          );
          window.dataLayer?.push({
            event: 'paypal_register_success',
            subscription_id: subId,
            product_name: productName,
            http_status: response.status
          });
        }

        const ApiUrl = process.env.ENVIRONMENT === Environment.prod
          ? 'https://api.verv.com'
          : 'https://api-test.verv.com';

        const fetchKey = async (attempt = 1) => {
          const responseKey = await fetch(
            `${ApiUrl}/v4/pp/purchase-event-idmp-key?group_bundle=everydoggy&subscription_id=${dataToSend.subscription_id}`,
            { method: "GET" }
          );
          if (responseKey.ok) {
            const data = await responseKey.json();
            if (data.data.purchase_event_idempotency_key) {
              const event = GTMQuizFunnelEvent.completePayment({
                paymentType: paymentType,
                trialPrice: `${price}`,
                productName: productName,
                eventId: data.data.purchase_event_idempotency_key,
              });
              analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
              return {};
            }
          }
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            return fetchKey(attempt + 1);
          } else {
            if (process.env.ENVIRONMENT === Environment.prod) {
              const error = new Error('purchase_event_idempotency_key not found after 3 attempts');
              Sentry.captureException(error, {
                level: 'warning',
                extra: {
                  data: responseKey || 'No data available',
                },
              });
            }
            return {};
          }
        };

        return fetchKey();
      }

      if (process.env.ENVIRONMENT === Environment.prod) {
        Sentry.captureMessage('registerPaypalSubscription failed', {
          level: 'error',
          tags: {
            module: 'EveryDoggyAPIServices.registerPaypalSubscription',
            http_status: response.status.toString()
          },
          extra: {
            // One distinct value per subscription — kept out of the tag index.
            subscription_id: subId,
            response_body: responseData || null
          }
        });

        analyticsEventsTracker.trackWithTarget(
          GTMOnboardingFunnelEvent.paypalRegisterFailure({
            subscriptionId: subId,
            productName,
            httpStatus: response.status,
            errorBody: JSON.stringify(responseData || {}),
          }),
          EVENT_TRACKERS.GTM
        );
        window.dataLayer?.push({
          event: 'paypal_register_failure',
          subscription_id: subId,
          product_name: productName,
          http_status: response.status,
          error_body: JSON.stringify(responseData || {})
        });
      }

      if (responseData) {
        if (
          responseData.non_field_errors &&
          Array.isArray(responseData.non_field_errors) &&
          responseData.non_field_errors.length > 0
        ) {
          errorMessage = responseData.non_field_errors[0];
        } else {
          const keys = Object.keys(responseData);
          if (keys.length > 0) {
            const key = keys[0];
            const value = responseData[key];
            errorMessage = Array.isArray(value) ? value[0] : String(value);
          }
        }
      }

      const parameters = {
        payment_system: 'paypal',
        product_name: productName,
        decline_reason: errorMessage
      };

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentDecline(parameters),
        EVENT_TRACKERS.GTM
      );

      throw {
        name: 'API error',
        message: errorMessage
      };
    },

    async registerPaypalSubscriptionV2(dataToSend, paymentType, productName, price) {
      const initialSubId = dataToSend?.paypal_plan_id || 'UNKNOWN_SUB_ID';

      if (process.env.ENVIRONMENT === Environment.prod) {
        Sentry.addBreadcrumb({
          message: 'Calling registerPaypalSubscription',
          category: 'api.call',
          data: { subscription_id: initialSubId }
        });
        analyticsEventsTracker.trackWithTarget(
          GTMOnboardingFunnelEvent.paypalRegisterAttempt({
            subscriptionId: initialSubId,
            productName,
          }),
          EVENT_TRACKERS.GTM
        );
        window.dataLayer?.push({
          event: 'paypal_register_attempt',
          subscription_id: initialSubId,
          product_name: productName,
        });
      }

      const response = await fetch(`${this.location}/core/api/v1/subscription/paypal/create/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Something went wrong';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      if (response.ok) {
        const responseSubId = responseData?.subscription_id || initialSubId;
        const approvalLink = responseData?.approval_link || '';

        if (!responseSubId) {
          throw {
            name: 'API error',
            message: 'PayPal subscription id is missing'
          };
        }

        if (process.env.ENVIRONMENT === Environment.prod) {
          analyticsEventsTracker.trackWithTarget(
            GTMOnboardingFunnelEvent.paypalRegisterSuccess({
              subscriptionId: responseSubId,
              productName,
              httpStatus: response.status,
            }),
            EVENT_TRACKERS.GTM
          );
          window.dataLayer?.push({
            event: 'paypal_register_success',
            subscription_id: responseSubId,
            product_name: productName,
            http_status: response.status
          });
        }

        return {
          subscription_id: responseSubId,
          approval_link: approvalLink,
        };
      }

      if (process.env.ENVIRONMENT === Environment.prod) {
        Sentry.captureMessage('registerPaypalSubscription failed', {
          level: 'error',
          tags: {
            module: 'EveryDoggyAPIServices.registerPaypalSubscription',
            http_status: response.status.toString()
          },
          extra: {
            // One distinct value per subscription — kept out of the tag index.
            subscription_id: initialSubId,
            response_body: responseData || null
          }
        });

        analyticsEventsTracker.trackWithTarget(
          GTMOnboardingFunnelEvent.paypalRegisterFailure({
            subscriptionId: initialSubId,
            productName,
            httpStatus: response.status,
            errorBody: JSON.stringify(responseData || {}),
          }),
          EVENT_TRACKERS.GTM
        );
        window.dataLayer?.push({
          event: 'paypal_register_failure',
          subscription_id: initialSubId,
          product_name: productName,
          http_status: response.status,
          error_body: JSON.stringify(responseData || {})
        });
      }

      if (responseData) {
        if (
          responseData.non_field_errors &&
          Array.isArray(responseData.non_field_errors) &&
          responseData.non_field_errors.length > 0
        ) {
          errorMessage = responseData.non_field_errors[0];
        } else {
          const keys = Object.keys(responseData);
          if (keys.length > 0) {
            const key = keys[0];
            const value = responseData[key];
            errorMessage = Array.isArray(value) ? value[0] : String(value);
          }
        }
      }

      const parameters = {
        payment_system: 'paypal',
        product_name: productName,
        decline_reason: errorMessage
      };

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentDecline(parameters),
        EVENT_TRACKERS.GTM
      );

      throw {
        name: 'API error',
        message: errorMessage
      };
    },

    async trackPaypalCompletePaymentEvent(subscriptionID, paymentType, productName, price) {
      if (!subscriptionID) {
        return {};
      }

      const ApiUrl = process.env.ENVIRONMENT === Environment.prod
        ? 'https://api.verv.com'
        : 'https://api-test.verv.com';

      const encodedSubscriptionID = encodeURIComponent(subscriptionID);

      const fetchKey = async (attempt = 1) => {
        let responseKey: Response | null = null;
        let thrownError: unknown = null;

        try {
          responseKey = await fetch(
            `${ApiUrl}/v4/pp/purchase-event-idmp-key?group_bundle=everydoggy&subscription_id=${encodedSubscriptionID}`,
            { method: 'GET' }
          );

          if (responseKey.ok) {
            const data = await responseKey.json();
            const eventId = data?.data?.purchase_event_idempotency_key;

            if (eventId) {
              const event = GTMQuizFunnelEvent.completePayment({
                paymentType: paymentType,
                trialPrice: `${price}`,
                productName: productName,
                eventId,
              });
              analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
              return {};
            }
          }
        } catch (error) {
          thrownError = error;
        }

        // Every outcome gets all three attempts. The backend's 400 may be a race with
        // subscription registration rather than a hard rejection, and there is no
        // per-status breakdown yet to tell those apart — see the http_status tag below.
        const status = responseKey?.status ?? null;

        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchKey(attempt + 1);
        }

        if (process.env.ENVIRONMENT === Environment.prod) {
          // Say what actually happened — "not found after 3 attempts" hid the status.
          // The fixed fingerprint keeps every variant of this message in one issue.
          let reason;
          if (status === null) {
            reason = 'network error';
          } else if (responseKey?.ok) {
            reason = `HTTP ${status}, key missing in body`;
          } else {
            reason = `HTTP ${status}`;
          }

          const error = new Error(`purchase_event_idempotency_key unavailable: ${reason}`);
          Sentry.captureException(error, {
            level: 'warning',
            fingerprint: ['purchase_event_idmp_key'],
            tags: {
              // A handful of distinct values, unlike subscription_id — safe as a tag,
              // and it makes the 400 / 5xx / network split a single Sentry query.
              http_status: String(status ?? 'network'),
            },
            extra: {
              subscription_id: subscriptionID,
              attempts: attempt,
              response_ok: responseKey?.ok || false,
              thrown_error: thrownError instanceof Error ? thrownError.message : String(thrownError || ''),
            },
          });
        }

        return {};
      };

      return fetchKey();
    },

    async checkoutPaypalSubscription(subscriptionID: string) {
      const subscriptionIDParameter = encodeURIComponent(subscriptionID);
      const redirectURL = `${this.location}/core/api/v1/plan/success/paypal/create_subscription/checkout/?subscription_id=${subscriptionIDParameter}`;
      return {
        redirect_url: redirectURL,
      };
    },

    async createOrderPaypal(dataToSend) {
      const response = await fetch(`${this.location}/core/api/v1/digital_product_purchase/paypal/create/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Something went wrong';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      if (response.ok) {
        return responseData;
      }

      if (responseData) {
        if (
          responseData.non_field_errors &&
          Array.isArray(responseData.non_field_errors) &&
          responseData.non_field_errors.length > 0
        ) {
          errorMessage = responseData.non_field_errors[0];
        } else {
          const keys = Object.keys(responseData);
          if (keys.length > 0) {
            const key = keys[0];
            const value = responseData[key];
            errorMessage = Array.isArray(value) ? value[0] : String(value);
          }
        }
      }

      const parameters = {
        payment_system: 'paypal',
        product_name: 'pdf product',
        decline_reason: errorMessage
      };

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentDecline(parameters),
        EVENT_TRACKERS.GTM
      );

      throw {
        name: 'API error',
        message: errorMessage,
      };
    },

    async registerPaypalOrder(dataToSend) {
      const response = await fetch(`${this.location}/core/api/v1/digital_product_purchase/paypal/catch/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Something went wrong';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      if (response.ok) {
        return {};
      }

      if (responseData) {
        if (
          responseData.non_field_errors &&
          Array.isArray(responseData.non_field_errors) &&
          responseData.non_field_errors.length > 0
        ) {
          errorMessage = responseData.non_field_errors[0];
        } else {
          const keys = Object.keys(responseData);
          if (keys.length > 0) {
            const key = keys[0];
            const value = responseData[key];
            errorMessage = Array.isArray(value) ? value[0] : String(value);
          }
        }
      }

      const parameters = {
        payment_system: 'paypal',
        product_name: 'pdf product',
        decline_reason: errorMessage
      };

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentDecline(parameters),
        EVENT_TRACKERS.GTM
      );

      throw {
        name: 'API error',
        message: errorMessage,
      };
    },

    async getStripeConfig() {
      return fetchConfigWithRetry(`${this.location}/core/api/v1/st/config`, {
        module: 'getStripeConfig',
        // Kept inside the parse step so the side effect stays where it was.
        transform: data => {
          window.STPKValue = data.publishableKey;
          return data;
        },
      });
    },

    // async createStripeSubscription(dataToSend, paymentType, productName, price, email) {
    //   // const response = await fetch(`${this.location}/core/api/v1/subscription/create_redirected/`, {
    //   const response = await fetch(`${this.location}/core/api/v2/subscription/stripe/create/`, {
    //     method: 'POST',
    //     body: JSON.stringify(dataToSend),
    //     headers: {
    //       Accept: 'application/json',
    //       'Content-Type': 'application/json',
    //       'Accept-Language': userLanguage,
    //     },
    //   });

    //     let responseData = null;
    //     let errorMessage = 'Unknown error';

    //     try {
    //       responseData = await response.json();
    //     } catch (e) {
    //       errorMessage = 'Something went wrong';
    //     }

    //     if (response.ok) {
    //       if (responseData?.purchase_event_idempotency_key) {
    //         const event = GTMQuizFunnelEvent.completePayment({
    //           paymentType: 'stripe',
    //           trialPrice: `${price}`,
    //           productName: productName,
    //           eventId: responseData.purchase_event_idempotency_key,
    //           email: email
    //         });
    //         analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
    //       } else {
    //         if (process.env.ENVIRONMENT === Environment.prod) {
    //           const error = new Error('purchase_event_idempotency_key not found');
    //           Sentry.captureException(error, {
    //             level: 'warning',
    //             extra: {
    //               data: responseData || 'No data available',
    //             },
    //           });
    //         }
    //       }
    //       return responseData;
    //     } else {
    //       if (responseData) {
    //         if (
    //           responseData.non_field_errors &&
    //           Array.isArray(responseData.non_field_errors) &&
    //           responseData.non_field_errors.length > 0
    //         ) {
    //           errorMessage = responseData.non_field_errors[0];
    //         } else {
    //           const keys = Object.keys(responseData);
    //           if (keys.length > 0) {
    //             const key = keys[0];
    //             const value = responseData[key];
    //             errorMessage = Array.isArray(value) ? value[0] : String(value);
    //           }
    //         }
    //       }

    //     const parameters = {
    //       payment_system: paymentType,
    //       product_name: productName,
    //       decline_reason: errorMessage
    //     };

    //     analyticsEventsTracker.trackWithTarget(
    //       GTMOnboardingFunnelEvent.paymentDecline(parameters),
    //       EVENT_TRACKERS.GTM
    //     );

    //     throw {
    //       name: 'API error',
    //       message: errorMessage
    //     };
  
    //   }
    // },

    async createStripeSubscription(dataToSend, paymentType, productName, price, email, stripe) {
      const response = await fetch(`${this.location}/core/api/v2/subscription/stripe/create/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Unknown error';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      // insert error message
      const extractErrorMessage = (data) => {
        if (!data) return 'Unknown error';
        if (data.non_field_errors?.length) return data.non_field_errors[0];
        // Read only fields that actually carry a message. This used to return
        // Object.keys(data)[0], so an error body led by `subscription_id` surfaced a
        // raw Stripe id as the "error" — both in Sentry and in GTM decline_reason.
        for (const key of ['detail', 'message', 'error', 'decline_reason']) {
          const value = data[key];
          if (Array.isArray(value)) {
            if (typeof value[0] === 'string' && value[0]) return value[0];
            continue;
          }
          if (typeof value === 'string' && value) return value;
        }
        return 'Unknown error';
      };

      // decline
      const trackDecline = (reason) => {
        const parameters = { payment_system: paymentType, product_name: productName, decline_reason: reason };
        analyticsEventsTracker.trackWithTarget(
          GTMOnboardingFunnelEvent.paymentDecline(parameters),
          EVENT_TRACKERS.GTM
        );
      };

      // error
      if (!response.ok) {
        errorMessage = extractErrorMessage(responseData);
        trackDecline(errorMessage);
        throw { name: 'API error', message: errorMessage };
      }

      // 3ds
      if (responseData.need_confirmation && responseData.client_secret) {
        try {
          const { error, paymentIntent } = await stripe.confirmCardPayment(responseData.client_secret);
          if (error) {
            trackDecline(error.message);
            throw { name: '3DS error', message: error.message };
          }
          const confirmResponse = await fetch(`${this.location}/core/api/v2/subscription/stripe/confirm/`, {
            method: 'POST',
            body: JSON.stringify({
              ...dataToSend,
              client_secret: responseData.client_secret,
              subscription_id: responseData.subscription_id,
              payment_intent_id: paymentIntent.id,
            }),
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'Accept-Language': userLanguage,
            },
          });

          const confirmData = await confirmResponse.json();

          if (!confirmResponse.ok) {
            errorMessage = extractErrorMessage(confirmData);
            trackDecline(errorMessage);
            throw { name: 'API error', message: errorMessage };
          }

          responseData = confirmData;
          
        } catch (e) {
          // The API error thrown above is already typed — rethrow it unchanged
          // instead of relabelling a backend decline as a 3DS failure.
          if (e?.name === 'API error') throw e;
          throw { name: '3DS error', message: e.message || '3DS confirmation failed' };
        }
      }

      // successful
      if (responseData?.purchase_event_idempotency_key) {
        const event = GTMQuizFunnelEvent.completePayment({
          paymentType: 'stripe',
          trialPrice: `${price}`,
          productName,
          eventId: responseData.purchase_event_idempotency_key,
          email,
        });
        analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
      }

      return responseData;
    },

    async purchaseStripeDigitalProduct(dataToSend, paymentType, productName) {
      const response = await fetch(`${this.location}/core/api/v1/digital_product_purchase/stripe/create/`, {
        method: 'POST',
        body: JSON.stringify(dataToSend),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': userLanguage,
        },
      });

      let responseData = null;
      let errorMessage = 'Something went wrong';

      try {
        responseData = await response.json();
      } catch (e) {
        errorMessage = 'Something went wrong';
      }

      if (response.ok) {
        return {};
      }

      if (responseData) {
        if (
          responseData.non_field_errors &&
          Array.isArray(responseData.non_field_errors) &&
          responseData.non_field_errors.length > 0
        ) {
          errorMessage = responseData.non_field_errors[0];
        } else {
          const keys = Object.keys(responseData);
          if (keys.length > 0) {
            const key = keys[0];
            const value = responseData[key];
            errorMessage = Array.isArray(value) ? value[0] : String(value);
          }
        }
      }

      const parameters = {
        payment_system: paymentType,
        product_name: 'pdf product',
        decline_reason: errorMessage
      };

      analyticsEventsTracker.trackWithTarget(
        GTMOnboardingFunnelEvent.paymentDecline(parameters),
        EVENT_TRACKERS.GTM
      );

      throw {
        name: 'API error',
        message: errorMessage
      };
    },

    async checkoutStripeSubscription(email: string) {
      const emailParameter = encodeURIComponent(email);
      const amplitudeDeviceId = getAmplitudeDeviceId();
      const amplitudeParameter = amplitudeDeviceId
      ? `&amplitude_device_id=${encodeURIComponent(amplitudeDeviceId)}`
      : '';
      
      const redirectURL =
      `${this.location}/core/api/v1/subscription/create_redirected/checkout/` +
      `?email=${emailParameter}${amplitudeParameter}`;
      return {
        redirect_url: redirectURL,
      };
    },

    async getWebCatalogProductConfig() {
      const response = await fetch(`${this.location}/core/api/v1/plans/config/`);
      const edPlanList: EDPlanConfigResponse = await response.json();

      // adapt to WebCatalogProduct
      const webCatalogConfig: WebCatalogConfigResponse = edPlanList.map(WebCatalogProductAdapter);
      return webCatalogConfig;
    },

    async getWebCatalogProduct(productId) {
      if (!productId) return null;

      return fetchConfigWithRetry(`${this.location}/core/api/v1/plans/${productId}/`, {
        module: 'getWebCatalogProduct',
        extra: {
          productId,
          visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        },
        // Adapter runs inside the parse try/catch, as before, so a malformed payload
        // is reported as parse_error rather than thrown at the caller.
        transform: (edPlan: EDPlanResponse) => WebCatalogProductAdapter(edPlan),
      });
    },

    async getWebCatalogDigitalProductList() {
      const response = await fetch(`${this.location}/core/api/v1/digital_product/list/`);
      const data = await response.json();
      return data;
    },

    async getWebCatalogDigitalProductOferList() {
      // Both call sites null-check the result, so failures degrade gracefully.
      // A non-2xx here stays silent, as it always has. A parse failure used to be
      // reported (the old try wrapped response.json() too, so it surfaced under the
      // network_error fingerprint) — keep reporting it, now under its own.
      return fetchConfigWithRetry(`${this.location}/core/api/v1/digital_product_offer/list/`, {
        module: 'getWebCatalogDigitalProductOferList',
        reportHttpError: false,
        reportParseError: true,
      });
    },

  };
}

function InMemoryCached(service: IEveryDoggyAPIServices): IEveryDoggyAPIServices {
  let paypalConfigCache: PaypalConfigResponse | null = null;
  let stripeConfigCache: StripeConfigResponse | null = null;
  let productsConfigCache: WebCatalogConfigResponse | null = null;
  const productsCache = new Map<string, WebCatalogProduct>();
  // In-flight requests keyed by productId. The resolved-value cache above only
  // populates after a fetch settles, so concurrent callers for the same product
  // (multiple offer components + the data middleware all mount on pageload) each
  // miss the cache and fire their own request — a cache stampede that Sentry flags
  // as an N+1 API Call on /core/api/v1/plans/*/. Sharing the pending promise
  // collapses those duplicates into a single network request per product.
  const productsInFlight = new Map<string, Promise<WebCatalogProduct | null>>();
  let digitalProductListCache: WebCatalogDigitalProductList | null = null;
  let digitalProductOferListCache: WebCatalogDigitalProductOferList | null = null;

  return {
    location: service.location,

    async getPaypalConfig() {
      if (paypalConfigCache) return paypalConfigCache;

      const data = await service.getPaypalConfig();
      if (data) paypalConfigCache = data;
      return data;
    },

    async registerPaypalSubscription(dataToSend, paymentType, productName, price) {
      return service.registerPaypalSubscription(dataToSend, paymentType, productName, price);
    },

    async registerPaypalSubscriptionV2(dataToSend, paymentType, productName, price) {
      return service.registerPaypalSubscriptionV2(dataToSend, paymentType, productName, price);
    },

    async trackPaypalCompletePaymentEvent(subscriptionID, paymentType, productName, price) {
      return service.trackPaypalCompletePaymentEvent(subscriptionID, paymentType, productName, price);
    },

    async checkoutPaypalSubscription(id) {
      return service.checkoutPaypalSubscription(id);
    },

    async registerPaypalOrder(dataToSend) {
      return service.registerPaypalOrder(dataToSend);
    },

    async createOrderPaypal(dataToSend) {
      return service.createOrderPaypal(dataToSend);
    },

    async getStripeConfig() {
      if (stripeConfigCache) return stripeConfigCache;

      const data = await service.getStripeConfig();
      if (data) stripeConfigCache = data;
      return data;
    },

    async checkoutStripeSubscription(dataToSend) {
      return service.checkoutStripeSubscription(dataToSend);
    },

    async createStripeSubscription(dataToSend, paymentType, productName, price, email, stripe) {
      return service.createStripeSubscription(dataToSend, paymentType, productName, price, email, stripe);
    },

    async purchaseStripeDigitalProduct(dataToSend, paymentType, productName) {
      return service.purchaseStripeDigitalProduct(dataToSend, paymentType, productName);
    },

    async getWebCatalogProductConfig() {
      if (productsConfigCache) return productsConfigCache;

      const data = await service.getWebCatalogProductConfig();
      productsConfigCache = data;
      return data;
    },

    async getWebCatalogProduct(productId) {
      const cachedProduct = productsCache.get(productId);
      if (cachedProduct) return cachedProduct;

      // Reuse an in-flight request for the same product instead of starting a new one.
      const pending = productsInFlight.get(productId);
      if (pending) return pending;

      const request = service.getWebCatalogProduct(productId)
        .then(data => {
          if (data) productsCache.set(productId, data);
          return data;
        })
        .finally(() => {
          // Drop the in-flight entry once settled; successful results live on in
          // productsCache, failures are free to be retried by the next caller.
          productsInFlight.delete(productId);
        });

      productsInFlight.set(productId, request);
      return request;
    },

    async getWebCatalogDigitalProductList() {
      if (digitalProductListCache) return digitalProductListCache;

      const data = await service.getWebCatalogDigitalProductList();
      digitalProductListCache = data;
      return data;
    },

    async getWebCatalogDigitalProductOferList() {
      if (digitalProductOferListCache) return digitalProductOferListCache;

      const data = await service.getWebCatalogDigitalProductOferList();
      digitalProductOferListCache = data;
      return data;
    },
  };
}

const EveryDoggyAPIServices: IEveryDoggyAPIServices = InMemoryCached(
  ComposeEveryDoggyAPIServices(process.env.API_URL, errorLogger)
);
export default EveryDoggyAPIServices;
